import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type AnswerMap } from "@njbsoft/form-logic";
import {
  carriedCureAnswers,
  isExecutionSealed,
  planPrefillSeed,
  sealedResponses,
  syncMetaKey,
  type BundleTarget,
  type ChangeRequestCategoryTemplate,
  type ChangeRequestSet,
  type OutboxAttachmentRow,
  type OutboxChangeRequestSetRow,
  type OutboxSubmissionRow,
  type StoredAssignment,
  type StoredFacility,
  type StoredKit,
  type StoredTemplateVersion,
  type StoredUtilityRequirements,
} from "@njbsoft/field-sync";
import { Button, EmptyState, PageSkeleton } from "@/components/ui";
import { DraftLockedElsewhere } from "@/components/draft-locked-elsewhere";
import { DeviceTestSession } from "@/components/tester/execution/device-test-session";
import { DraftSidecarGate } from "@/components/tester/execution/draft-sidecar-gate";
import { InspectionSession } from "@/components/tester/execution/inspection-session";
import {
  onlineFacility,
  onlineReadContext,
  onlineTarget,
  wireKitToStored,
  type OnlineReadContext,
} from "@/components/tester/execution/online-arm";
import {
  TesterExecution,
  type TesterExecutionProps,
} from "@/components/tester/execution/tester-execution";
import { useFieldworkStorage } from "@/lib/fieldwork/storage-provider";
import { parseStoredDefinition } from "@/lib/fieldwork/stored-definition";
import { isInspectionKind } from "@/lib/inspection-kinds";
import { useDraftLock } from "@/hooks/use-draft-lock";
import { useFacilityDevices } from "@/hooks/use-facility-devices";
import { useIsOnline } from "@/hooks/use-is-online";
import { useMySubmission } from "@/hooks/use-my-submission";
import { useMySubmissions } from "@/hooks/use-my-submissions";
import { useMyWorkAssignment } from "@/hooks/use-my-work-assignment";
import { useMyTestKits } from "@/hooks/use-my-test-kits";
import { useToast } from "@/hooks/use-toast";

/**
 * What the start picker hands the form. `kit` is the gauge it chose — its
 * presence is what makes the kit card READ-ONLY here (spec decision A: the
 * gauge is picked where readiness is evaluated, not after). `supersedes` is
 * the rejected document a resubmission replaces.
 */
interface ExecuteSearch {
  kit?: string;
  supersedes?: string;
}

export const Route = createFileRoute("/_company/my-work/$assignmentId/execute")({
  validateSearch: (search: Record<string, unknown>): ExecuteSearch => ({
    kit: typeof search.kit === "string" && search.kit !== "" ? search.kit : undefined,
    supersedes:
      typeof search.supersedes === "string" && search.supersedes !== ""
        ? search.supersedes
        : undefined,
  }),
  component: TesterExecutePage,
});

/** A settled-but-empty online read context — the absent-facility arm. */
const EMPTY_ONLINE_CTX: OnlineReadContext = {
  facilityValues: {},
  deviceValues: {},
  collections: {},
};

/** One atomic settle of the local read chain — no partial renders. */
interface LocalReads {
  bundleRow: StoredAssignment | null;
  version: StoredTemplateVersion | null;
  target: BundleTarget | null;
  facility: StoredFacility | null;
  outboxRows: OutboxSubmissionRow[];
  /** Each outbox row's attachment rows, by its client submission id. */
  rowAttachments: Record<string, OutboxAttachmentRow[]>;
  kits: StoredKit[];
  sealed: boolean;
  draftLocked: boolean;
  /** The draft row's persisted kit FK — the composer's kit seed on a reload. */
  draftKitId: string | null;
  /**
   * What the session's panel reads beside the draft: the Device-of-record
   * panel's facts, and the utility's requirements — the Devices found
   * card's editor reads them, and the Violations found card its correction
   * window and hazard types.
   */
  setRows: OutboxChangeRequestSetRow[];
  bundleSets: ChangeRequestSet[];
  syncMeta: string | null;
  requirements: StoredUtilityRequirements | null;
  categoryTemplate: ChangeRequestCategoryTemplate | null;
  /**
   * Whether this settle already evaluated the cure carry's API source to its
   * final value. The mount hold below reads it: the composer may only mount
   * once the carried answers are ON DISK, because its draft restore runs once.
   */
  cureCarrySettled: boolean;
  /**
   * Whether the prefill seed has run (this pass or an earlier one). Online the
   * seed's inputs are the server reads, which settle after the local chain —
   * false here holds the skeleton until they have, the same mount-once
   * discipline `cureCarrySettled` enforces for the carry.
   */
  seedSettled: boolean;
}

/**
 * The execution screen, online-first with an offline arm. ONLINE the server is
 * truth: the assignment row, the kit set and the read context come from the
 * field queries (the detail screen's and the claim screen's sources), and only
 * the template definition reads the local cache — BY PINNED VERSION ID, since
 * a published version is immutable and the pin is the frozen one the server
 * row carries (never "latest": version resolution under tester claims is the
 * server's, per the RLS lesson). A cache miss online pulls ONCE; while any
 * online read is unsettled the screen holds a skeleton — the
 * "not downloaded to this device" state and its Sync Now button render only
 * when the effective row is absent (offline, or every source failed). OFFLINE
 * everything loads from the on-device bundle snapshot exactly as before, and
 * the shared `SubmissionComposer` renders the document over the tester's
 * offline port set in both arms — regulatory writes stay outbox-first.
 *
 * The seed and the composer's draft restore each run ONCE per mount, on the
 * arm that first settles: a mid-mount connectivity flip swaps the read sources
 * under the mounted composer but never re-runs the seed (a post-mount seed
 * write would sit on disk unseen under the first autosave — the C2 carry
 * lesson). The sealed verdict is computed from freshly-read facts on every
 * local refresh, and the prefill seed runs (and persists, unless sealed)
 * BEFORE the composer mounts, so the draft the composer restores is already
 * seeded. A sealed screen's document follows the same arm split: online it is
 * the server's newest submission for the assignment (the cross-device review),
 * offline the local newest outbox row. A resubmission (`?supersedes=`) rides
 * that same seam with the rejected predecessor's own answers — minus the
 * evidence types a resubmission retakes — and, when the predecessor lives only
 * on the server, holds the skeleton until the API read has seeded them.
 */
function TesterExecutePage() {
  const { t } = useTranslation(["field-work"]);
  const { assignmentId } = Route.useParams();
  const { kit: lockedKitId, supersedes } = Route.useSearch();
  const { storage, engine } = useFieldworkStorage();
  const draftLock = useDraftLock(assignmentId);
  const toast = useToast();

  const [reads, setReads] = useState<LocalReads | null>(null);

  // The draft port's loud-failure surface, shared by the session panel's
  // writes (the Device-of-record panel's, the Devices found and Violations
  // found cards'). The transient toast alone is not enough for the
  // stuck-restoring arm — a restore failure holds `isRestoring` true and the
  // form inert with nothing on screen saying why — so every failure also
  // LATCHES the inline notice (it clears only with a reload, which is the
  // recovery step the copy names). The callback is deliberately
  // dependency-free: the draft hook is memoized on it alongside the storage
  // identity.
  const [draftStorageFailed, setDraftStorageFailed] = useState(false);
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const notifyDraftError = useCallback((error: unknown) => {
    setDraftStorageFailed(true);
    toastRef.current.error("field-work:execute.draftStorageFailed");
    console.error("tester draft storage failed", error);
  }, []);

  // The cure carry's API source: the rejected predecessor this device never
  // sent (another device did), read through the History detail's own query.
  // Armed only once the local read chain has settled AND no synced outbox row
  // carries the predecessor — offline, or with the local row in hand, the hook
  // stays disabled and the fill path issues no request at all.
  const isOnline = useIsOnline();
  const cureApiSource =
    supersedes !== undefined &&
    isOnline &&
    reads !== null &&
    !reads.outboxRows.some((row) => row.serverSubmissionId === supersedes);
  const priorSubmission = useMySubmission(cureApiSource ? supersedes : null);
  const priorApiResponses = priorSubmission.data?.responses ?? null;
  const cureApiPending = cureApiSource && priorSubmission.isPending;

  // The online arm's reads: the assignment row, the kit set and the facility
  // roster from the field queries the detail and claim screens already run.
  // All three pause while offline (TanStack gates on onlineManager — the same
  // source useIsOnline mirrors), so the offline fill path still issues zero
  // network requests. A FAILED assignment read falls back to the synced bundle
  // row — the detail screen's dual-source precedence — while it is merely in
  // flight the screen holds its skeleton (the seed must not run on arm data a
  // pending query is about to replace).
  const assignmentQuery = useMyWorkAssignment(assignmentId);
  const wireRow = assignmentQuery.data ?? null;
  const assignmentFailed = assignmentQuery.isError;
  const kitsQuery = useMyTestKits();
  const devicesQuery = useFacilityDevices(wireRow?.facility_id ?? "");
  const devicesView = devicesQuery.data ?? null;
  const devicesFailed = devicesQuery.isError;
  const onlineCtx = useMemo(() => {
    if (wireRow === null) return null;
    // A failed roster read is settled, not pending: identity labels ride the
    // assignment row itself, and the seed proceeds on an empty context — the
    // same absent-facility arm the offline bundle has for an uncached one.
    if (devicesView === null) return devicesFailed ? EMPTY_ONLINE_CTX : null;
    return onlineReadContext(devicesView, wireRow.device_installation_id ?? null);
  }, [wireRow, devicesView, devicesFailed]);

  // The sealed review's online arm (D6): a screen sealed with no local outbox
  // row — the test was submitted on ANOTHER device, or the pull pruned the
  // completed assignment's rows after the fact — has no local document to
  // render. Online the server serves it: the assignment's NEWEST submission is
  // resolved through the archive list (the detail screen's rejection-card
  // pattern — no per-assignment wire filter exists) and its detail read is the
  // same History source the cure carry rides. The queries arm only when sealed
  // AND online with the wire row in hand, so the offline path issues zero
  // requests and a failed assignment read keeps the local arm's document.
  const sealedOnline = isOnline && reads !== null && reads.sealed && wireRow !== null;
  // The wire's maximum page is the widest window this read can carry; an
  // assignment whose every submission sits outside it settles onto the empty
  // sealed document. Accepted: the window is the newest iterations, which are
  // the sealed review's subject, and walking pages for a pre-prod archive's
  // depth is machinery the degradation does not owe.
  const newestSealedListQuery = useMySubmissions(
    { org_id: wireRow?.org_id, sort_by: "performed_at", sort_dir: "desc", limit: 100 },
    { enabled: sealedOnline },
  );
  // "Newest" means the newest ITERATION, not the newest performed_at: the test
  // date is a client-supplied bound answer, so a date-correcting redo can sort
  // strictly BEFORE the predecessor it replaces. A row the server has marked
  // superseded is never the review's document — the list's own server-computed
  // chain link says so.
  const newestSealedRow = sealedOnline
    ? ((newestSealedListQuery.data?.items ?? []).find(
        (row) => row.assignment_id === assignmentId && row.superseded_by_submission_id === null,
      ) ?? null)
    : null;
  const newestSealedId = newestSealedRow?.id ?? null;
  const sealedDetailQuery = useMySubmission(sealedOnline ? newestSealedId : null);
  const sealedServerResponses = sealedOnline ? (sealedDetailQuery.data?.responses ?? null) : null;
  // The sealed-online document's mount hold: with no local row the offline
  // document is EMPTY, and rendering it while the server read is in flight
  // would flash exactly the degraded review this arm exists to kill. The
  // skeleton holds until the reads settle (a failure settles it too — onto
  // today's empty rendering). Only the no-local-row case holds: a local newest
  // row serves the screen meanwhile and the settled server document swaps in
  // place, never unmounting the composer it rendered under.
  const sealedListSettled =
    newestSealedListQuery.data !== undefined || newestSealedListQuery.isError;
  const sealedDetailSettled =
    newestSealedId === null || sealedDetailQuery.data !== undefined || sealedDetailQuery.isError;
  const sealedOnlinePending =
    sealedOnline &&
    reads !== null &&
    reads.outboxRows.length === 0 &&
    !(sealedListSettled && sealedDetailSettled);

  // The pinned definition, served from the local cache BY VERSION ID: a
  // published version is immutable, so the cache entry is byte-identical to
  // the server's forever — no freshness dimension, and never a client-side
  // "latest" resolution (under tester claims version resolution is the
  // server's; the pin the wire row carries IS its answer). Online a miss
  // pulls ONCE per pinned id — the run writes the whole bundle, this row
  // included; offline the bundle arm's own read is the source and no pull
  // fires. undefined = not yet read (hold); null = read and genuinely absent.
  const pinnedVersionId = isOnline && wireRow !== null ? wireRow.template_version_id : null;
  const [onlineVersion, setOnlineVersion] = useState<StoredTemplateVersion | null | undefined>(
    undefined,
  );
  const missPullFor = useRef<string | null>(null);
  // The pinned id this effect last armed. A connectivity blip
  // (online→offline→online) re-arms the effect for the SAME id — and a
  // published version is immutable, so the row already served for that id is
  // still the row: re-opening the read would un-serve it for one storage get,
  // trip the unavailable gate, and UNMOUNT the composer (its draft restore
  // runs once; keystrokes sitting in the debounced autosave ride only the
  // unmount flush). Only an unsettled read (a blip racing the first one) or a
  // genuinely different id re-opens.
  const armedPinnedId = useRef<string | null>(null);
  useEffect(() => {
    if (pinnedVersionId === null || storage === null) return;
    if (armedPinnedId.current === pinnedVersionId && onlineVersion !== undefined) return;
    const idChanged = armedPinnedId.current !== pinnedVersionId;
    armedPinnedId.current = pinnedVersionId;
    let cancelled = false;
    if (idChanged) setOnlineVersion(undefined);
    void storage.getTemplateVersion(pinnedVersionId).then((row) => {
      if (cancelled) return;
      if (row !== null) {
        setOnlineVersion(row);
        return;
      }
      if (engine === null || missPullFor.current === pinnedVersionId) {
        setOnlineVersion(null);
        return;
      }
      missPullFor.current = pinnedVersionId;
      void engine
        .syncFresh()
        .then(() => storage.getTemplateVersion(pinnedVersionId))
        .then((pulled) => {
          if (!cancelled) setOnlineVersion(pulled);
        })
        .catch(() => {
          // A failed storage re-read is a miss: the unavailable state below is
          // the honest render, with the button as its retry.
          if (!cancelled) setOnlineVersion(null);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [pinnedVersionId, storage, engine, onlineVersion]);

  // Which arm's inputs may run the prefill seed this pass. The arm that first
  // settles owns the seed for the whole mount — a mid-mount connectivity flip
  // swaps the read sources under the mounted composer but never re-seeds (the
  // composer restores once; a post-mount seed write would sit on disk unseen
  // under the first autosave — the C2 carry lesson, held the same way).
  const seedArmRef = useRef<"online" | "local" | null>(null);

  const refreshLocal = useCallback(async () => {
    // Before the provider hands over live handles there is nothing local to
    // read; the effect re-runs on its own when the store opens.
    if (storage === null) return;
    const [row, draft, submissions, bundleKits] = await Promise.all([
      storage.getBundleAssignment(assignmentId),
      storage.getDraft(assignmentId),
      storage.listForAssignment(assignmentId),
      storage.listBundleKits(),
    ]);
    const pinned = row
      ? await storage.getTemplateVersion(row.assignment.template_version_id)
      : null;
    const premise = row
      ? await storage.getTarget(row.assignment.facility_id, row.assignment.device_installation_id)
      : null;
    const bundleFacility = row ? await storage.getBundleFacility(row.assignment.facility_id) : null;
    const [setRows, bundleSets, syncMeta, requirements, categoryTemplate, attachmentLists] =
      await Promise.all([
        storage.listChangeRequestSetRows(),
        storage.listBundleChangeRequestSets(),
        storage.getSyncMeta(syncMetaKey({ mode: "field" })),
        row ? storage.getUtilityRequirements(row.assignment.org_id) : null,
        row ? storage.getCategoryTemplate(row.assignment.org_id, "testable_assembly") : null,
        Promise.all(
          submissions.map((submission) =>
            storage.listAttachmentsForSubmission(submission.clientSubmissionId),
          ),
        ),
      ]);

    // Which arm feeds this settle. Online the wire row wins; a failed read
    // falls back to the bundle; a read still in flight owns the screen as a
    // hold (nothing seeds on arm data a pending query is about to replace).
    const arm: "online" | "local" | "pending" =
      wireRow !== null ? "online" : isOnline && !assignmentFailed ? "pending" : "local";
    const onlineSeed =
      arm === "online" && onlineVersion !== undefined && onlineCtx !== null
        ? { version: onlineVersion, ctx: onlineCtx }
        : null;
    // An arm's inputs are ready when its seed can run: the local arm's reads
    // are in hand, the online arm's once the pinned cache row and the roster
    // context have settled. Only a READY arm may claim the once-per-mount
    // seed — a not-yet-ready arm claiming it would lift the mount hold with
    // no seed on disk.
    const armReady = arm === "local" || onlineSeed !== null;
    // Only the arm that first settled may seed — a mid-mount flip never does.
    const seedArm: "online" | "local" | null =
      arm === "pending" || !armReady || (seedArmRef.current !== null && seedArmRef.current !== arm)
        ? null
        : arm;
    if (seedArm !== null) seedArmRef.current = seedArm;

    // Resolved from the freshly-read facts, never from the previous render's
    // state — the one place the shell runs the predicate itself, because the
    // prefill seed needs the verdict before the composer exists to compute it.
    // Online the server row is the status of record, the local row's
    // optimistic overlay still winning until the next pull restores server
    // truth — the same precedence the detail screen reads.
    const statusOf =
      arm === "online" && wireRow !== null ? wireRow.status : (row?.assignment.status ?? null);
    const effectiveStatus = row?.localStatus ?? statusOf;
    const sealed = isExecutionSealed(
      draft?.locked === true,
      submissions,
      effectiveStatus,
      supersedes,
    );

    // Seed read-source answers from the effective read context with the
    // target's device keys over it, ABSENT KEYS ONLY, and let `planPrefillSeed`
    // decide whether the result may reach disk — it REFUSES the save for a
    // sealed assignment (cleanup deletes the draft; a later save would INSERT a
    // fresh UNLOCKED draft for an already-submitted assignment). Idempotent: a
    // visibility re-run reloads the just-seeded draft and gets the same map
    // back. Persisting BEFORE the composer mounts is what lets its draft
    // restore pick the seed up.
    const seedAssignment =
      seedArm === "online" && wireRow !== null ? wireRow : (row?.assignment ?? null);
    const seedVersionRow =
      seedArm === "online" && onlineSeed !== null ? onlineSeed.version : pinned;
    const seedDefinition =
      seedVersionRow !== null ? parseStoredDefinition(seedVersionRow.definitionJson) : null;
    // A cure opens on the tester's own prior answers, not an empty form: the
    // rejected predecessor's responses seed the draft through the SAME seam
    // the facility seed rides. The local synced row is the source of record —
    // it is never deleted and satisfies the carry offline; the API detail
    // covers a predecessor another device sent. Document and signature
    // answers never carry (a resubmission retakes its evidence; the rejected
    // set's blobs are deleted with it), and the merge is absent-keys-only —
    // the draft's own answers, a resumed cure's edits, always win.
    let carry: AnswerMap | undefined;
    if (seedAssignment !== null && seedDefinition !== null && supersedes !== undefined) {
      const priorRow = submissions.find((candidate) => candidate.serverSubmissionId === supersedes);
      const priorResponses =
        priorRow !== undefined ? sealedResponses(priorRow.payloadJson) : priorApiResponses;
      if (priorResponses !== null) carry = carriedCureAnswers(seedDefinition, priorResponses);
    }
    if (seedAssignment !== null && seedDefinition !== null && seedArm !== null) {
      const readContext =
        seedArm === "online" && onlineSeed !== null
          ? { ...onlineSeed.ctx.facilityValues, ...onlineSeed.ctx.deviceValues }
          : { ...bundleFacility?.readContext, ...premise?.device_read_context };
      const collections =
        seedArm === "online" && onlineSeed !== null
          ? onlineSeed.ctx.collections
          : (bundleFacility?.collections ?? {});
      const plan = planPrefillSeed({
        definition: seedDefinition,
        draftAnswers: draft?.answers ?? {},
        facilityId: seedAssignment.facility_id,
        readContext,
        collections,
        sealed,
        carry,
      });
      if (plan.persist) await storage.saveDraft(assignmentId, plan.answers);
    }

    // The picker's gauge is the selection of record from the moment the form
    // opens, so it is persisted to the draft BEFORE the composer mounts — the
    // same seam the context-bar pick writes through. Without this the locked
    // kit would live only in the URL: a reload restores the draft's stale FK,
    // and the sealed payload's test_kit_id disagrees with the gauge whose
    // calibration the pre-flight actually cleared. Never on a sealed record —
    // its regulatory kit is already written.
    let effectiveDraftKitId = draft?.testKitId ?? null;
    if (lockedKitId !== undefined && !sealed && effectiveDraftKitId !== lockedKitId) {
      await storage.setDraftKit(assignmentId, lockedKitId);
      effectiveDraftKitId = lockedKitId;
    }

    setReads({
      bundleRow: row,
      version: pinned,
      target: premise,
      facility: bundleFacility,
      outboxRows: submissions,
      rowAttachments: Object.fromEntries(
        submissions.map((submission, i) => [
          submission.clientSubmissionId,
          attachmentLists[i] ?? [],
        ]),
      ),
      kits: bundleKits,
      sealed,
      draftLocked: draft?.locked === true,
      draftKitId: effectiveDraftKitId,
      setRows,
      bundleSets,
      syncMeta,
      requirements,
      categoryTemplate,
      // A settle that ran while the armed API read was still in flight has
      // NOT evaluated the carry to its final value — the mount hold below
      // stays up until a later settle (the query's settle re-runs this
      // callback through its deps) carries the answers onto disk first.
      cureCarrySettled: cureApiSource && !cureApiPending,
      // False only while the active arm's seed inputs are still in flight
      // (its assignment read pending, or the pinned row / roster context not
      // yet settled) AND nothing has seeded yet — once a seed has run the
      // composer stays mounted across every arm flip.
      seedSettled: seedArmRef.current !== null || armReady,
    });
  }, [
    storage,
    assignmentId,
    lockedKitId,
    supersedes,
    priorApiResponses,
    cureApiPending,
    cureApiSource,
    isOnline,
    wireRow,
    assignmentFailed,
    onlineVersion,
    onlineCtx,
  ]);

  // Mobile re-reads on screen focus; the browser substitution is a mount read
  // plus a visibility listener, so a backgrounded tab picks up whatever the
  // sync engine wrote while it was away.
  //
  // Gated on the draft lock, because the read chain is not read-only: it
  // persists the prefill seed. Running it from a tab that does not own the
  // draft would be the cross-tab draft write the lock exists to prevent, and
  // the other two arms render nothing that needs the reads anyway.
  useEffect(() => {
    if (draftLock !== "held") return;
    void refreshLocal();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refreshLocal();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshLocal, draftLock]);

  // Engine state straight off the provider handle, never through the sync-bar
  // hook: this screen renders no bar, and mounting the hook here would
  // register a SECOND set of run triggers beside the shell provider's single
  // one — the fill path itself still issues no requests; the shell's own
  // triggers are what keep the world around it fresh.
  const subscribeSyncState = useCallback(
    (listener: () => void) =>
      engine !== null ? engine.subscribeSyncState(listener) : () => undefined,
    [engine],
  );
  const isSyncing = useSyncExternalStore(
    subscribeSyncState,
    () => engine !== null && engine.isSyncRunning(),
    () => false,
  );

  // The unavailable state's call-to-action, and the tail of every chip retry:
  // run the engine, then re-read the local rows so the screen reflects the
  // run's outcome. A fresh run, because a retry or a cure has just returned its
  // row to the queue and a run already in flight may have drained past it.
  // Online it is also the miss-pull's retry — a fresh run may have written the
  // pinned version row the one-shot pull failed to bring.
  const handleSync = useCallback(() => {
    if (engine === null) return;
    void engine.syncFresh().then(async () => {
      if (pinnedVersionId !== null && storage !== null) {
        setOnlineVersion(await storage.getTemplateVersion(pinnedVersionId));
      }
      await refreshLocal();
    });
  }, [engine, refreshLocal, pinnedVersionId, storage]);

  // Chip retry and the chip's evidence cure. A `blocked` row goes back to the
  // queue FIRST — `retryBlocked` is the only way out of that state and the
  // drain skips it, so a run alone would leave it where it is.
  const handleRetry = useCallback(
    async (row: OutboxSubmissionRow) => {
      if (storage !== null && row.state === "blocked") {
        await storage.retryBlocked(row.clientSubmissionId);
      }
      handleSync();
    },
    [storage, handleSync],
  );

  // The effective row set, memoized before the gates (the loaded arm's ports
  // key off these identities). The server row serves the assignment family
  // whenever it is in hand (its query data survives a connectivity blip); the
  // other families are server-served when their read LANDED and
  // bundle-served while in flight or after a failed read — dual-source per
  // family, the detail screen's precedence. Two rules hold across every arm:
  // an in-flight read never UN-serves a row the mounted composer is standing
  // on (its restore runs once — the blip discipline), and the VERSION is
  // always a PINNED row: the cache read's while in flight, and on a settled
  // miss the bundle's pin row — the same pin the wire row carries for started
  // work (the freeze), so no arm ever serves an unpinned or "latest"
  // definition. One arm split is deliberate: on a FAILED roster read the
  // render rows fall back to the bundle's facility/target while the seed
  // proceeds on the (empty) server context — display items show the bundle's
  // values, prefill items that only the bundle context carried are never
  // seeded (absent-keys display items never seed, so nothing is overwritten
  // under the composer).
  const rosterIn = devicesView !== null;
  const localStatus = reads?.bundleRow?.localStatus ?? null;
  const effectiveBundleRow = useMemo<StoredAssignment | null>(
    () => (wireRow === null ? null : { assignment: wireRow, localStatus }),
    [wireRow, localStatus],
  );
  const effectiveVersion =
    wireRow === null ? (reads?.version ?? null) : (onlineVersion ?? reads?.version ?? null);
  const effectiveFacility = useMemo(
    () =>
      wireRow !== null && rosterIn
        ? onlineFacility(wireRow, devicesView, onlineCtx ?? EMPTY_ONLINE_CTX)
        : (reads?.facility ?? null),
    [wireRow, rosterIn, devicesView, onlineCtx, reads?.facility],
  );
  const effectiveTarget = useMemo(
    () =>
      wireRow !== null && rosterIn
        ? onlineTarget(wireRow, devicesView, onlineCtx?.deviceValues ?? {})
        : (reads?.target ?? null),
    [wireRow, rosterIn, devicesView, onlineCtx, reads?.target],
  );
  const effectiveKits = useMemo(() => {
    if (wireRow === null) return reads?.kits ?? [];
    return kitsQuery.data !== undefined
      ? kitsQuery.data.items.map(wireKitToStored)
      : (reads?.kits ?? []);
  }, [wireRow, kitsQuery.data, reads?.kits]);
  const definition = useMemo(
    () => (effectiveVersion ? parseStoredDefinition(effectiveVersion.definitionJson) : null),
    [effectiveVersion],
  );

  // A sibling tab owns this draft: the read-only notice, and no editable
  // composer anywhere in the tree — mounting a disabled one would still run
  // its autosave effects against the holder's answers.
  if (draftLock === "heldElsewhere") {
    return <DraftLockedElsewhere />;
  }

  if (draftLock === "acquiring" || reads === null) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }

  // Online, the skeleton owns the screen until the arm's inputs settle — the
  // pre-pull state is NEVER a storage complaint. The hold lifts for good once
  // a seed has run: the composer's restore happens once, so a connectivity
  // flip mid-mount must leave the mounted tree mounted.
  if (!reads.seedSettled && seedArmRef.current === null) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }

  const bundleRow = effectiveBundleRow ?? reads.bundleRow;
  const version = effectiveVersion;
  const facility = effectiveFacility;
  const target = effectiveTarget;
  const kits = effectiveKits;

  // A device test answers the Device-of-record panel, which reads the target's
  // installation: without it the test cannot be submitted, so it is
  // unavailable until a sync brings it.
  const deviceTarget =
    version !== null &&
    version.targetKind === "device_test" &&
    target?.device_installation_id !== undefined
      ? target
      : null;

  // The unavailable state — offline, or every source failed online (a failed
  // assignment read with no bundle row; a pinned version the one-shot pull
  // did not bring). The button is the offline affordance and the retry.
  if (
    bundleRow === null ||
    version === null ||
    definition === null ||
    (version.targetKind === "device_test" && deviceTarget === null)
  ) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState
          title={t("field-work:detail.title")}
          description={t("field-work:execute.assignmentUnavailable")}
        />
        <div className="flex justify-center">
          <Button onClick={handleSync} loading={isSyncing}>
            {t("field-work:sync.syncNow")}
          </Button>
        </div>
      </div>
    );
  }

  // The sealed-online document's mount hold (see its derivation above): no
  // composer mounts until the server document settles or the arm falls back.
  if (sealedOnlinePending) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }

  // The cure carry's mount hold. The composer's draft restore runs ONCE, so a
  // carry that lands after the mount would sit on disk unseen and the first
  // autosave would overwrite it — the armed API read must settle and its
  // answers reach the draft BEFORE the composer exists. The local row needs
  // no hold: its carry rides the same read chain that settles `reads` in the
  // first place.
  if (cureApiSource && !reads.cureCarrySettled) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }

  // The storage handle is non-null whenever a bundle row was read off it, and
  // the composer's draft port needs it in both arms.
  if (storage === null) return null;

  const execution: TesterExecutionProps = {
    storage,
    assignmentId,
    // The lock says "the picker already chose"; the CARD resolves that choice
    // out of the effective kit set. A gauge registered since the last sync is
    // in neither the wire set nor the bundle, so locking on the param alone
    // would print "no kit selected" with no picker to fix it — the lock
    // degrades to a live control instead.
    kitLocked: lockedKitId !== undefined && kits.some((kit) => kit.id === lockedKitId),
    supersedesSubmissionId: supersedes,
    bundleRow,
    version,
    definition,
    target,
    facility,
    deviceReadContext: target?.device_read_context,
    outboxRows: reads.outboxRows,
    rowAttachments: reads.rowAttachments,
    // The server-served sealed document while online; absent (offline, or the
    // read failed) the local newest row stays the review's source.
    sealedResponses: sealedServerResponses ?? undefined,
    utilityName:
      reads.requirements?.orgName ?? facility?.orgName ?? bundleRow.assignment.org_name ?? "",
    kits,
    sealed: reads.sealed,
    draftLocked: reads.draftLocked,
    draftKitId: reads.draftKitId,
    onRetry: handleRetry,
    draftStorageFailed,
    notifyDraftError,
    refresh: refreshLocal,
  };

  if (deviceTarget !== null) {
    return (
      <DraftSidecarGate assignmentId={assignmentId} loadFailedKey="panel.loadFailed">
        {(sidecar, update) => (
          <DeviceTestSession
            execution={execution}
            target={deviceTarget}
            reads={reads}
            sidecar={sidecar}
            update={update}
          />
        )}
      </DraftSidecarGate>
    );
  }
  if (isInspectionKind(version.targetKind)) {
    return (
      <DraftSidecarGate assignmentId={assignmentId} loadFailedKey="foundDevices.loadFailed">
        {(sidecar, update) => (
          <InspectionSession
            execution={execution}
            requirements={reads.requirements}
            sidecar={sidecar}
            update={update}
          />
        )}
      </DraftSidecarGate>
    );
  }
  return <TesterExecution {...execution} />;
}
